import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Cancel as DeclineIcon } from '@mui/icons-material';
import { commissionApi } from '../../services/api';

const REASON_SUGGESTIONS = [
  'Not the same product',
  'Different brand',
  'Fraudulent activity suspected',
  'Cookie expired',
  'Invalid order',
  'Duplicate commission request',
];

export default function DeclineConfirmDialog({ open, item, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setSelectedSuggestion('custom');
      setError(null);
      setResult(null);
    }
  }, [open]);

  const handleSuggestionChange = (event) => {
    const value = event.target.value;
    setSelectedSuggestion(value);
    if (value && value !== 'custom') {
      setReason(value);
    } else {
      setReason('');
    }
  };

  const handleDecline = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for decline');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await commissionApi.decline(item.id, { reason: reason.trim() });

      if (response.success) {
        setResult(response);
      } else {
        setError(response.error || 'Failed to decline');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (result) {
      onSuccess();
    } else {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeclineIcon color="error" />
          Decline Commission
        </Box>
      </DialogTitle>

      <DialogContent>
        {result ? (
          // Success state
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <DeclineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Commission Declined
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The commission has been declined with the provided reason.
            </Typography>
          </Box>
        ) : (
          <>
            {(item?.status === 'auto_approved' || item?.status === 'manually_approved') ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                This commission was previously <strong>{item.status === 'auto_approved' ? 'auto-approved' : 'manually approved'}</strong>. Declining it will reverse the approval.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to decline this commission. This action cannot be undone.
              </Alert>
            )}

            {/* Item summary */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Order: {item?.order_number || '-'}
              </Typography>
              <Typography variant="body2">
                {item?.product_name || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Commission: {item?.commission_amount?.toFixed(2) || '-'}
              </Typography>
            </Box>

            {/* Reason selection */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select a reason (optional)</InputLabel>
              <Select
                value={selectedSuggestion}
                onChange={handleSuggestionChange}
                label="Select a reason (optional)"
              >
                <MenuItem value="custom">Custom reason</MenuItem>
                {REASON_SUGGESTIONS.map((suggestion) => (
                  <MenuItem key={suggestion} value={suggestion}>
                    {suggestion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Reason input */}
            <TextField
              label="Reason for decline"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              required
              error={!!error && !reason.trim()}
              helperText={!reason.trim() && error ? 'Reason is required' : ''}
              placeholder="Enter the reason for declining this commission..."
            />

            {error && reason.trim() && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {result ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleDecline}
              color="error"
              variant="contained"
              disabled={loading || !reason.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <DeclineIcon />}
            >
              {loading ? 'Declining...' : 'Decline'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
